import i18n, { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { hourMs, minuteMs } from "verdi-common/timeConstants";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { useLocale } from "@/hooks/use-locale";
import { NAMESPACES } from "@/i18n/constants";
import { cn } from "@/utils/class-name";
import { BASE_WINDOW_HOURS, MAX_EXTENSION_HOURS } from "../use-on-demand-event-data";
import { EVENT_BLOCK_HEIGHT } from "./constants";
import { GhostEventBlock } from "./ghost-event-block";

export type VisualizationItems = {
    zoneId: string;
    zoneName: string;
    events: TimelineItem[];
};

export type TimelineItem = {
    startTime: Date;
    endTime: Date;
    isSetEvent?: boolean;
    /** Command group id when present; undefined when event has no command group. Only match when both this and nearestFutureEventId are defined. */
    id: string | undefined;
};

export type TimeRange = {
    start: Date;
    end: Date;
};

/**
 * Pending user action to preview in the visualization before submission.
 */
export type PendingActionDetails =
    | { type: "create"; zoneId: string; durationMs: number; startTime?: Date }
    | { type: "edit"; zoneId: string; newEndTime: Date }
    | { type: "delete"; zoneId: string };

const LANE_HEIGHT = 70;
const TIME_ROW_HEIGHT = 30;

// TODO: Set event color is shared with the scheduler — consider moving into theme.css (oklch(0.5859 0.1961 320.26))
const BLOCK_STYLES = {
    default: "bg-[#E4E5E2] border-muted-foreground/75",
    set: "bg-set-event border-muted-foreground/75",
    delete: "border-destructive bg-destructive/20",
    editFade: "opacity-25 bg-muted-foreground/75 border-muted-foreground/75",
} as const;

function getBlockColorClass(
    isPendingDelete: boolean | undefined,
    isPendingEdit: boolean | undefined,
    event: TimelineItem,
): string {
    if (isPendingDelete) return BLOCK_STYLES.delete;
    if (isPendingEdit) return BLOCK_STYLES.editFade;
    if (event.isSetEvent) return BLOCK_STYLES.set;
    return BLOCK_STYLES.default;
}

/**
 * Computes the chart time scale, uses a base window of BASE_WINDOW_HOURS hours extended by MAX_EXTENSION_HOURS hours
 * if any event ends past the initial base window.
 */
function getDynamicTimeRange(now: Date, timelineItems: VisualizationItems[], paddingBeforeMs: number): TimeRange {
    const baseStart = new Date(now.getTime() - paddingBeforeMs);
    const baseEnd = new Date(baseStart.getTime() + BASE_WINDOW_HOURS * hourMs);

    const allEvents = timelineItems.flatMap((item) => item.events);
    const latestEndMs = allEvents.length ? Math.max(...allEvents.map((e) => e.endTime.getTime())) : baseEnd.getTime();

    const extensionMs = Math.min(MAX_EXTENSION_HOURS * hourMs, Math.max(0, latestEndMs - baseEnd.getTime()));
    const end = new Date(baseEnd.getTime() + extensionMs);

    return { start: baseStart, end };
}

const LABEL_COUNT = 4;

/**
 * Returns LABEL_COUNT evenly-spaced ticks so the tick range fully covers [range.start, range.end].
 * Start is snapped down to the hour, end is snapped up to the hour, so events near the ends are
 * never clipped
 */
function getTicks(range: TimeRange): Date[] {
    const rangeStartMs = range.start.getTime();
    const rangeEndMs = range.end.getTime();

    const firstTickMs = Math.floor(rangeStartMs / hourMs) * hourMs;
    let lastTickMs = Math.ceil(rangeEndMs / hourMs) * hourMs;
    lastTickMs = Math.max(lastTickMs, firstTickMs + hourMs);

    const ticks: Date[] = [];
    const spanMs = lastTickMs - firstTickMs;
    for (let i = 0; i < LABEL_COUNT; i++) {
        const t = firstTickMs + (i / (LABEL_COUNT - 1)) * spanMs;
        ticks.push(new Date(t));
    }
    return ticks;
}

function formatTime(date: Date, locale: string): string {
    return date.toLocaleTimeString(locale, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
}

function formatTimeRange(startTime: Date, endTime: Date, locale: string): string {
    const durationMin = Math.round((endTime.getTime() - startTime.getTime()) / minuteMs);
    if (durationMin <= 0) return "";
    const minuteLabel = i18n.t("time.minute.short", { ns: NAMESPACES.COMMON });
    const startStr = startTime.toLocaleString(locale, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
    const endStr = endTime.toLocaleTimeString(locale, {
        hour: "numeric",
        minute: "2-digit",
    });
    return `${durationMin} ${minuteLabel} · ${startStr} – ${endStr}`;
}

function formatGhostTooltip(startTime: Date, endTime: Date, locale: string): string {
    return formatTimeRange(startTime, endTime, locale);
}

function formatEventTooltip(event: TimelineItem, locale: string, t: TFunction): string {
    const prefix = event.isSetEvent ? `${t("set-event-label")} · ` : "";
    return `${prefix}${formatTimeRange(event.startTime, event.endTime, locale)}`;
}

function getPendingActionForZone(pending: PendingActionDetails | null, zoneId: string, nowMs: number) {
    if (!pending || pending.zoneId !== zoneId) return null;
    if (pending.type === "create") {
        const startMs = pending.startTime ? pending.startTime.getTime() : nowMs;
        return {
            type: "create" as const,
            startTime: new Date(startMs),
            endTime: new Date(startMs + pending.durationMs),
        };
    }
    if (pending.type === "edit") return { type: "edit" as const, newEndTime: pending.newEndTime };
    return { type: "delete" as const };
}

interface EventVisualizationProps {
    nearestFutureEventId: string | undefined;
    visualizationItems: VisualizationItems[];
    laneHeight?: number;
    timeBeforeToInclude: number; // visualization should include this much time (in ms) before the current time
    /** Width of the visualization; use "100%" to fill container (default), or a number for fixed px. */
    width?: number | string;
    nowDate: Date;
    /** Pending user action to preview in the visualization before submission. */
    pendingActionDetails: PendingActionDetails | null;
}

export function EventVisualization({
    nearestFutureEventId,
    visualizationItems,
    laneHeight = LANE_HEIGHT,
    width,
    nowDate,
    timeBeforeToInclude,
    pendingActionDetails,
}: EventVisualizationProps) {
    const { t } = useTranslation(NAMESPACES.FEATURES.CARDS, { keyPrefix: "zone.on-demand-irrigation.visualization" });
    const locale = useLocale();

    const timeRange = useMemo(
        () => getDynamicTimeRange(nowDate, visualizationItems, timeBeforeToInclude),
        [nowDate, visualizationItems, timeBeforeToInclude],
    );

    const ticks = useMemo(() => getTicks(timeRange), [timeRange]);

    // Use the tick range (hour-snapped) as the display scale so that label positions,
    // grid lines, the now-line, and event blocks all share the same coordinate system.
    const displayRangeStartMs = ticks[0].getTime();
    const displayRangeDurationMs = ticks[ticks.length - 1].getTime() - displayRangeStartMs;
    const toPercent = (date: Date): number => {
        const ms = date.getTime() - displayRangeStartMs;
        return Math.max(0, Math.min(100, (ms / displayRangeDurationMs) * 100));
    };

    const currentTimePercent = toPercent(nowDate);

    if (visualizationItems.length === 0) {
        return (
            <div
                className={"rounded border border-gray-300 bg-gray-50 p-4 text-gray-500 text-sm"}
                style={{ width: width ?? "100%" }}
            >
                {t("no-events-to-display")}
            </div>
        );
    }

    return (
        <div className={"flex w-full max-w-full min-w-0 flex-col px-8"} style={{ width: width ?? "100%" }}>
            <div className={"flex min-w-0"}>
                {/* Timeline (right) */}
                <div className={"relative flex min-w-0 flex-1 flex-col border-b border-gray-400"}>
                    <div
                        className={"relative flex-1"}
                        style={{
                            minHeight: TIME_ROW_HEIGHT + visualizationItems.length * laneHeight,
                        }}
                    >
                        {/* Diamond marker for current time: rendered above overflow container so it sits on top of the horizontal bar */}
                        <div
                            className={"absolute z-30 size-2 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-gray-700"}
                            style={{ left: `${currentTimePercent}%`, top: TIME_ROW_HEIGHT }}
                            aria-hidden
                        />

                        {/* Time labels row: outside overflow so left/right labels are never clipped; z-index keeps on top */}
                        <div
                            className={
                                "absolute left-0 right-0 top-0 z-20 flex items-center border-b border-gray-400 text-xs text-gray-600"
                            }
                            style={{ height: TIME_ROW_HEIGHT }}
                            role={"img"}
                        >
                            {ticks.map((tick, i) => {
                                let left: string;
                                if (i === 0) {
                                    left = "0%";
                                } else if (i === ticks.length - 1) {
                                    left = "100%";
                                } else {
                                    left = `${toPercent(tick)}%`;
                                }
                                return (
                                    <span
                                        key={tick.getTime()}
                                        className={
                                            "absolute whitespace-nowrap rounded-full bg-gray-700 px-2 py-0.5 text-[10px] text-white"
                                        }
                                        style={{ left, transform: "translateX(-50%)" }}
                                    >
                                        {formatTime(tick, locale)}
                                    </span>
                                );
                            })}
                        </div>

                        {/* Grid + now line + event lanes: clipped so events don't overflow */}
                        <div className={"absolute inset-0 overflow-hidden"}>
                            {/* Left and right edge bars, same height as grid lines */}
                            <div
                                className={"absolute bottom-0 left-0 w-px bg-gray-400"}
                                style={{ top: TIME_ROW_HEIGHT / 2 }}
                                aria-hidden
                            />
                            <div
                                className={"absolute bottom-0 right-0 w-px bg-gray-400"}
                                style={{ top: TIME_ROW_HEIGHT / 2 }}
                                aria-hidden
                            />

                            {/* Grid lines: interior ticks only, extending up to the midpoint of the label row */}
                            {ticks.slice(1, -1).map((tick) => (
                                <div
                                    key={tick.getTime()}
                                    className={"absolute bottom-0 w-px bg-gray-400"}
                                    style={{ left: `${toPercent(tick)}%`, top: TIME_ROW_HEIGHT / 2 }}
                                    aria-hidden
                                />
                            ))}

                            {/* Current time: thin vertical line */}
                            <div
                                className={"absolute z-10 w-px bg-gray-700"}
                                style={{
                                    left: `${currentTimePercent}%`,
                                    transform: "translateX(-50%)",
                                    top: TIME_ROW_HEIGHT,
                                    bottom: 0,
                                }}
                                aria-hidden
                            />

                            {/* Event lanes (one row per zone, aligned with label) */}
                            {visualizationItems.map(({ zoneId, events }, index) => {
                                const pendingAction = getPendingActionForZone(
                                    pendingActionDetails,
                                    zoneId,
                                    nowDate.getTime(),
                                );

                                const nearestFutureEvent =
                                    nearestFutureEventId !== undefined
                                        ? visualizationItems
                                              .find((item) => item.zoneId === zoneId)
                                              ?.events.find((event) => event.id === nearestFutureEventId)
                                        : undefined;

                                return (
                                    <div
                                        key={zoneId}
                                        className={
                                            "absolute left-0 right-0 flex items-center border-b border-gray-200 px-0.5 last:border-b-0"
                                        }
                                        style={{
                                            top: TIME_ROW_HEIGHT + index * laneHeight,
                                            height: laneHeight,
                                        }}
                                    >
                                        {events.map((event) => {
                                            const isNearestFutureEvent =
                                                nearestFutureEventId !== undefined && event.id === nearestFutureEventId;
                                            const leftPct = toPercent(event.startTime);
                                            const widthPct = Math.max(0, toPercent(event.endTime) - leftPct);
                                            const eventKey = `${zoneId}-${event.startTime.getTime()}-${event.endTime.getTime()}`;
                                            const isPendingDelete =
                                                pendingAction?.type === "delete" && isNearestFutureEvent;
                                            const isPendingEdit =
                                                pendingAction?.type === "edit" && isNearestFutureEvent;

                                            const blockColorClass = getBlockColorClass(
                                                isPendingDelete,
                                                isPendingEdit,
                                                event,
                                            );

                                            return (
                                                <Popover key={eventKey}>
                                                    <PopoverTrigger
                                                        nativeButton={false}
                                                        openOnHover
                                                        delay={100}
                                                        render={
                                                            <div
                                                                className={cn(
                                                                    "absolute min-w-0 rounded-sm border transition-[filter]",
                                                                    EVENT_BLOCK_HEIGHT,
                                                                    !isPendingDelete &&
                                                                        !isPendingEdit &&
                                                                        "hover:brightness-105",
                                                                    blockColorClass,
                                                                    isNearestFutureEvent &&
                                                                        !isPendingDelete &&
                                                                        !isPendingEdit &&
                                                                        "ring-1 ring-gray-700 ring-offset-0",
                                                                )}
                                                                style={{
                                                                    left: `${leftPct}%`,
                                                                    width: `${widthPct}%`,
                                                                }}
                                                            />
                                                        }
                                                    />
                                                    <PopoverPopup tooltipStyle className={"border-border"}>
                                                        {formatEventTooltip(event, locale, t)}
                                                    </PopoverPopup>
                                                </Popover>
                                            );
                                        })}

                                        {/* Pending edit: ghost block at the projected new time range */}
                                        {pendingAction?.type === "edit" && nearestFutureEvent !== undefined && (
                                            <GhostEventBlock
                                                leftPct={toPercent(nearestFutureEvent.startTime)}
                                                widthPct={
                                                    toPercent(pendingAction.newEndTime) -
                                                    toPercent(nearestFutureEvent.startTime)
                                                }
                                                variant={"edit"}
                                                tooltipContent={formatGhostTooltip(
                                                    nearestFutureEvent.startTime,
                                                    pendingAction.newEndTime,
                                                    locale,
                                                )}
                                            />
                                        )}

                                        {/* Pending create: ghost block starting at custom start time (or current time) */}
                                        {pendingAction?.type === "create" && (
                                            <GhostEventBlock
                                                leftPct={toPercent(pendingAction.startTime)}
                                                widthPct={
                                                    toPercent(pendingAction.endTime) -
                                                    toPercent(pendingAction.startTime)
                                                }
                                                variant={"create"}
                                                tooltipContent={formatGhostTooltip(
                                                    pendingAction.startTime,
                                                    pendingAction.endTime,
                                                    locale,
                                                )}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
